import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

import AgriPanel from "components/AgriTheme/AgriPanel";
import AgriStatCard from "components/AgriTheme/AgriStatCard";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";
import MDBox from "components/MDBox";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

const doughnutCenterText = {
  id: "doughnutCenterText",
  afterDraw(chart, _args, options) {
    if (!options) return;

    const { ctx, chartArea } = chart;
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#f8fafc";
    ctx.font = "700 26px sans-serif";
    ctx.fillText(String(options.value || 0), centerX, centerY - 10);
    ctx.fillStyle = "#8fa7c4";
    ctx.font = "500 12px sans-serif";
    ctx.fillText(options.label || "total users", centerX, centerY + 17);
    ctx.restore();
  },
};

const cartesianChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(7, 18, 32, 0.96)",
      borderColor: "rgba(143, 167, 196, 0.25)",
      borderWidth: 1,
      titleColor: "#f8fafc",
      bodyColor: "#d7e5ff",
      displayColors: false,
      padding: 10,
      cornerRadius: 8,
    },
  },
  scales: {
    x: {
      border: { display: false },
      grid: { display: false },
      ticks: {
        color: "#8fa7c4",
        font: { size: 10 },
        autoSkip: true,
        maxRotation: 0,
        maxTicksLimit: 12,
      },
    },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: "rgba(143, 167, 196, 0.12)", drawTicks: false },
      ticks: { color: "#8fa7c4", font: { size: 10 }, precision: 0, padding: 8 },
    },
  },
};

const PERIODS = [
  { value: "day", label: "Day" },
  { value: "date_range", label: "Date Range" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const METRIC_ICONS = {
  app_opens: "phone_android",
  agriboost_opens: "bolt",
  agriboost_card_views: "visibility",
  pyq_opens: "history_edu",
  pyq_attempts: "task_alt",
  ar_opens: "rule",
  ar_attempts: "fact_check",
  leaderboard_opens: "leaderboard",
  resources_opens: "library_books",
};

const METRIC_TONES = {
  app_opens: "linear-gradient(135deg, #db2777 0%, #831843 100%)",
  agriboost_opens: "linear-gradient(135deg, #22c55e 0%, #14532d 100%)",
  agriboost_card_views: "linear-gradient(135deg, #84cc16 0%, #365314 100%)",
  pyq_opens: "linear-gradient(135deg, #f59e0b 0%, #92400e 100%)",
  pyq_attempts: "linear-gradient(135deg, #fb7185 0%, #9f1239 100%)",
  ar_opens: "linear-gradient(135deg, #38bdf8 0%, #075985 100%)",
  ar_attempts: "linear-gradient(135deg, #8b5cf6 0%, #4c1d95 100%)",
  leaderboard_opens: "linear-gradient(135deg, #facc15 0%, #854d0e 100%)",
  resources_opens: "linear-gradient(135deg, #06b6d4 0%, #164e63 100%)",
};

const getCurrentValues = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return {
    day: `${year}-${month}-${day}`,
    week: `${year}-${month}-${day}`,
    month: `${year}-${month}`,
    year: String(year),
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPeriodInputType = (period) => {
  if (period === "year") return "number";
  if (period === "month") return "month";
  return "date";
};

function FeatureAnalytics() {
  const currentValues = useMemo(getCurrentValues, []);
  const [period, setPeriod] = useState("month");
  const [periodValue, setPeriodValue] = useState(currentValues.month);
  const [rangeStart, setRangeStart] = useState(currentValues.day);
  const [rangeEnd, setRangeEnd] = useState(currentValues.day);
  const [chartMetric, setChartMetric] = useState("all");
  const [userMetric, setUserMetric] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState({
    metrics: [],
    summary: [],
    timeline: [],
    overview: {},
    users: { docs: [] },
  });
  const [notification, setNotification] = useState({ open: false, message: "" });

  useEffect(() => {
    if (init() !== "success") return undefined;

    setLoading(true);
    axios
      .get(Constants.getUrls.getFeatureAnalytics, {
        params: {
          period,
          value: periodValue,
          start_date: period === "date_range" ? rangeStart : undefined,
          end_date: period === "date_range" ? rangeEnd : undefined,
          metric: userMetric,
          page,
          limit: rowsPerPage,
        },
      })
      .then((response) => {
        setAnalytics(response?.data?.data || {});
      })
      .catch((error) => {
        setNotification({
          open: true,
          message: error?.response?.data?.message || "Unable to load feature analytics.",
        });
      })
      .finally(() => setLoading(false));

    return undefined;
  }, [page, period, periodValue, rangeEnd, rangeStart, rowsPerPage, userMetric]);

  const handlePeriodChange = (nextPeriod) => {
    setPeriod(nextPeriod);
    if (nextPeriod !== "date_range") {
      setPeriodValue(currentValues[nextPeriod]);
    }
    setPage(1);
  };

  const timeline = analytics?.timeline || [];
  const doughnutRows = (analytics?.summary || []).filter(
    (item) => Number(item.unique_users || 0) > 0
  );
  const doughnutChartData = {
    labels: doughnutRows.length ? doughnutRows.map((item) => item.label) : ["No activity"],
    datasets: [
      {
        data: doughnutRows.length
          ? doughnutRows.map((item) => Number(item.unique_users || 0))
          : [1],
        backgroundColor: doughnutRows.length
          ? doughnutRows.map((item) => item.color)
          : ["#24364c"],
        borderColor: "#0b1624",
        borderWidth: doughnutRows.length ? 2 : 0,
        hoverBorderColor: "#f8fafc",
        hoverBorderWidth: 2,
        spacing: 1,
      },
    ],
  };
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: doughnutRows.length > 0,
        backgroundColor: "rgba(7, 18, 32, 0.96)",
        borderColor: "rgba(143, 167, 196, 0.25)",
        borderWidth: 1,
        titleColor: "#f8fafc",
        bodyColor: "#d7e5ff",
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context) => ` ${context.label}: ${context.raw} users`,
        },
      },
      doughnutCenterText: {
        value: analytics?.overview?.unique_users || 0,
        label: "total users",
      },
    },
  };
  const activityMetrics =
    chartMetric === "all"
      ? analytics?.metrics || []
      : (analytics?.metrics || []).filter((item) => item.key === chartMetric);
  const activityChartData = {
    labels: timeline.map((item) => item.label),
    datasets: activityMetrics.map((metric) => ({
      label: metric.label,
      data: timeline.map((item) => Number(item?.[metric.key] || 0)),
      backgroundColor: metric.color,
      borderColor: metric.color,
      borderWidth: 1,
      borderRadius: 5,
      borderSkipped: false,
      maxBarThickness: chartMetric === "all" ? 12 : 28,
    })),
  };
  const activityChartOptions = {
    ...cartesianChartOptions,
    plugins: {
      ...cartesianChartOptions.plugins,
      legend: {
        display: chartMetric === "all",
        position: "bottom",
        labels: {
          color: "#c7d8eb",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 7,
          boxHeight: 7,
          padding: 14,
          font: { size: 10 },
        },
      },
      tooltip: {
        ...cartesianChartOptions.plugins.tooltip,
        displayColors: chartMetric === "all",
      },
    },
  };
  const activityChartWidth = Math.max(720, timeline.length * (chartMetric === "all" ? 58 : 38));
  const trendChartData = {
    labels: timeline.map((item) => item.label),
    datasets: [
      {
        label: "Unique users",
        data: timeline.map((item) => Number(item?.unique_users || 0)),
        borderColor: "#8b5cf6",
        backgroundColor: "rgba(139, 92, 246, 0.16)",
        pointBackgroundColor: "#f8fafc",
        pointBorderColor: "#8b5cf6",
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        borderWidth: 3,
        fill: true,
        tension: 0.35,
      },
    ],
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title="Feature Analytics" />

      <MDBox mb={2} display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
        <TextField
          select
          size="small"
          label="View By"
          value={period}
          onChange={(event) => handlePeriodChange(event.target.value)}
          sx={{ minWidth: 140 }}
        >
          {PERIODS.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
        {period === "date_range" ? (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={rangeStart}
              onChange={(event) => {
                setRangeStart(event.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: rangeEnd }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={rangeEnd}
              onChange={(event) => {
                setRangeEnd(event.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: rangeStart }}
            />
          </>
        ) : (
          <TextField
            size="small"
            type={getPeriodInputType(period)}
            label={PERIODS.find((item) => item.value === period)?.label || "Period"}
            value={periodValue}
            onChange={(event) => {
              setPeriodValue(event.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={period === "year" ? { min: 2020, max: Number(currentValues.year) } : {}}
          />
        )}
        <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
          {analytics?.range?.start_date || "-"} to {analytics?.range?.end_date || "-"}
        </MDTypography>
      </MDBox>

      <MDBox
        mb={2.5}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: `repeat(${Math.max(analytics?.summary?.length || 1, 1)}, 145px)`,
            xl: `repeat(${Math.max(analytics?.summary?.length || 1, 1)}, minmax(0, 1fr))`,
          },
          gap: 1,
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        {(analytics?.summary || []).map((item) => (
          <AgriStatCard
            key={item.key}
            icon={METRIC_ICONS[item.key] || "analytics"}
            label={item.label}
            value={item.unique_users}
            helper={`${item.events} events`}
            tone={METRIC_TONES[item.key] || "linear-gradient(135deg, #334155, #0f172a)"}
            compact
            dense
          />
        ))}
      </MDBox>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={4}>
          <AgriPanel
            title="Unique Users by Feature"
            subtitle="A user can appear in more than one feature."
          >
            <MDBox height="220px" mb={2} position="relative">
              <Doughnut
                data={doughnutChartData}
                options={doughnutOptions}
                plugins={[doughnutCenterText]}
              />
            </MDBox>
            <MDBox display="grid" gap={0.8}>
              {(analytics?.summary || []).map((item) => (
                <MDBox key={item.key} display="flex" justifyContent="space-between" gap={1}>
                  <MDBox display="flex" alignItems="center" gap={0.8} minWidth={0}>
                    <MDBox width="9px" height="9px" borderRadius="50%" sx={{ background: item.color }} />
                    <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                      {item.label}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#fff" }}>
                    {item.unique_users}
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
          </AgriPanel>
        </Grid>

        <Grid item xs={12} lg={8}>
          <AgriPanel
            title="Feature Activity"
            subtitle="Compare all tracked features or focus on one feature."
            actions={
              <TextField
                select
                size="small"
                value={chartMetric}
                onChange={(event) => setChartMetric(event.target.value)}
                sx={{ minWidth: 190 }}
              >
                <MenuItem value="all">All Features</MenuItem>
                {(analytics?.metrics || []).map((item) => (
                  <MenuItem key={item.key} value={item.key}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            }
          >
            <MDBox sx={{ overflowX: "auto", pb: 0.5 }}>
              <MDBox height="325px" width={`${activityChartWidth}px`}>
                <Bar data={activityChartData} options={activityChartOptions} />
              </MDBox>
            </MDBox>
          </AgriPanel>
        </Grid>

        <Grid item xs={12}>
          <AgriPanel
            title="Unique User Trend"
            subtitle="Distinct users engaging with at least one tracked feature in each period."
          >
            <MDBox height="280px">
              <Line data={trendChartData} options={cartesianChartOptions} />
            </MDBox>
          </AgriPanel>
        </Grid>

        <Grid item xs={12}>
          <AgriPanel
            title="User Activity"
            subtitle="Names link to user details. Contact information is not shown."
            actions={
              <TextField
                select
                size="small"
                label="Feature"
                value={userMetric}
                onChange={(event) => {
                  setUserMetric(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 210 }}
              >
                <MenuItem value="all">All Features</MenuItem>
                {(analytics?.metrics || []).map((item) => (
                  <MenuItem key={item.key} value={item.key}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            }
            noPadding
          >
            <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
              <Table
                size="small"
                sx={{
                  minWidth: 1380,
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": {
                    color: "#c7d8eb",
                    borderBottom: "1px solid rgba(126,170,220,0.1)",
                    fontSize: "0.72rem",
                    padding: "11px 12px",
                    verticalAlign: "middle",
                  },
                  "& .MuiTableHead-root": {
                    display: "table-header-group",
                    padding: 0,
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    color: "#fff",
                    fontWeight: 700,
                    background: "rgba(17,35,56,0.94)",
                    whiteSpace: "nowrap",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    background: "rgba(56,189,248,0.05)",
                  },
                }}
              >
                <colgroup>
                  <col style={{ width: 220 }} />
                  <col style={{ width: 180 }} />
                  <col style={{ width: 65 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 70 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 170 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>College</TableCell>
                    <TableCell align="center">App</TableCell>
                    <TableCell align="center">Boost Open</TableCell>
                    <TableCell align="center">Cards</TableCell>
                    <TableCell align="center">PYQ Open</TableCell>
                    <TableCell align="center">PYQ Attempt</TableCell>
                    <TableCell align="center">AR Open</TableCell>
                    <TableCell align="center">AR Attempt</TableCell>
                    <TableCell align="center">Leaderboard</TableCell>
                    <TableCell align="center">Resources</TableCell>
                    <TableCell>Last Activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={12}>Loading analytics...</TableCell></TableRow>
                  ) : !(analytics?.users?.docs || []).length ? (
                    <TableRow><TableCell colSpan={12}>No user activity found for this period.</TableCell></TableRow>
                  ) : (
                    analytics.users.docs.map((user) => (
                      <TableRow key={user.user_id} hover>
                        <TableCell>
                          <MDTypography
                            component={Link}
                            to={`/user-details/${user.user_id}`}
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: "#fff", textDecoration: "none", whiteSpace: "nowrap" }}
                          >
                            {user.name}
                          </MDTypography>
                        </TableCell>
                        <TableCell>{user?.college?.short_name || user?.college?.name || "-"}</TableCell>
                        <TableCell align="center">{user.metrics?.app_opens || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.agriboost_opens || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.agriboost_card_views || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.pyq_opens || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.pyq_attempts || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.ar_opens || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.ar_attempts || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.leaderboard_opens || 0}</TableCell>
                        <TableCell align="center">{user.metrics?.resources_opens || 0}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(user.last_activity_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <MDBox
              px={2}
              py={1.5}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
              sx={{ borderTop: "1px solid rgba(126,170,220,0.1)" }}
            >
              <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
                <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
                  Rows per page
                </MDTypography>
                <TextField
                  select
                  size="small"
                  value={rowsPerPage}
                  onChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                  sx={{ width: 78 }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </TextField>
                <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                  {analytics?.users?.totalDocs
                    ? `${(analytics.users.page - 1) * analytics.users.limit + 1}-${Math.min(
                        analytics.users.page * analytics.users.limit,
                        analytics.users.totalDocs
                      )} of ${analytics.users.totalDocs}`
                    : "0 records"}
                </MDTypography>
              </MDBox>
              {analytics?.users?.totalPages > 1 ? (
                <Pagination
                  page={analytics.users.page}
                  count={analytics.users.totalPages}
                  onChange={(_, nextPage) => setPage(nextPage)}
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                  sx={agriPaginationSx}
                />
              ) : null}
            </MDBox>
          </AgriPanel>
        </Grid>
      </Grid>

      <MDSnackbar
        color="warning"
        icon="notifications"
        title="AgriQuiz"
        content={notification.message}
        dateTime=""
        open={notification.open}
        onClose={() => setNotification({ open: false, message: "" })}
        close={() => setNotification({ open: false, message: "" })}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default FeatureAnalytics;
